import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, UserCog } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const blank = { display: "", teams: "", match: "" };

/** Productivity → Settings → Monitored users. Add, edit or remove the people
 *  whose monitored activity is reported on. */
export function ProdRosterCard({ canManage }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/productivity/roster/manage")
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Couldn't load the monitored users"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (row, patch) => {
    try {
      await api.put(`/productivity/roster/${row.key}`, {
        display: patch.display ?? row.configured_display,
        teams: (patch.teams ?? row.teams.join(", ")).split(",").map((t) => t.trim()).filter(Boolean),
        match: (patch.match ?? row.match.join(", ")).split(",").map((t) => t.trim()).filter(Boolean),
      });
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that change");
      load();
    }
  };

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/productivity/roster", {
        display: draft.display.trim(),
        teams: draft.teams.split(",").map((t) => t.trim()).filter(Boolean),
        match: draft.match.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setDraft(blank);
      load();
      toast.success(`${draft.display} is now monitored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that person");
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Stop monitoring ${row.display}? Their activity disappears from every tab and report.`)) return;
    await api.delete(`/productivity/roster/${row.key}`);
    load();
    toast.success(`${row.display} is no longer monitored`);
  };

  return (
    <Card className="p-4" data-testid="prod-roster-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <UserCog className="h-4 w-4" /> Monitored users
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Only these people appear in the reports. &ldquo;Match on&rdquo; is what we look for in the
        monitored account &mdash; an email is safest; a bare first name can catch the wrong account.
      </p>

      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
        <>
          <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-400">
                <tr>
                  <th className="px-2 py-2 font-semibold">Name</th>
                  <th className="px-2 py-2 font-semibold">Teams</th>
                  <th className="px-2 py-2 font-semibold">Match on</th>
                  <th className="px-2 py-2 font-semibold">Monitored account</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {data.people.map((row) => (
                  <tr key={row.key} className="border-t border-slate-100 dark:border-slate-800 align-top"
                    data-testid={`prod-roster-row-${row.key}`}>
                    <td className="px-2 py-2">
                      <Input defaultValue={row.configured_display} disabled={!canManage}
                        onBlur={(e) => e.target.value.trim() !== row.configured_display && save(row, { display: e.target.value })}
                        className="h-8" data-testid={`prod-roster-name-${row.key}`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input defaultValue={row.teams.join(", ")} disabled={!canManage}
                        onBlur={(e) => e.target.value !== row.teams.join(", ") && save(row, { teams: e.target.value })}
                        className="h-8" data-testid={`prod-roster-teams-${row.key}`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input defaultValue={row.match.join(", ")} disabled={!canManage}
                        onBlur={(e) => e.target.value !== row.match.join(", ") && save(row, { match: e.target.value })}
                        className="h-8 font-mono text-xs" data-testid={`prod-roster-match-${row.key}`} />
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {row.matched_agents.length ? (
                        <span className="text-slate-600 dark:text-slate-300" data-testid={`prod-roster-matched-${row.key}`}>
                          {row.matched_agents.join(", ")}
                        </span>
                      ) : (
                        <span className="text-amber-600" data-testid={`prod-roster-unmatched-${row.key}`}>
                          no monitored account matched
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {canManage && (
                        <button type="button" onClick={() => remove(row)}
                          className="text-slate-300 dark:text-slate-500 transition-colors hover:text-rose-600"
                          data-testid={`prod-roster-delete-${row.key}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canManage && (
            <div className="mt-3 space-y-2" data-testid="prod-roster-add">
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-44">
                  <label className="text-xs text-slate-400 dark:text-slate-400">Name</label>
                  <Input value={draft.display} onChange={(e) => setDraft({ ...draft, display: e.target.value })}
                    placeholder="e.g. Sam Lee" className="h-8" data-testid="prod-roster-new-name" />
                </div>
                <div className="w-44">
                  <label className="text-xs text-slate-400 dark:text-slate-400">Teams</label>
                  <Input value={draft.teams} onChange={(e) => setDraft({ ...draft, teams: e.target.value })}
                    placeholder="Service Delivery" className="h-8" data-testid="prod-roster-new-teams" />
                </div>
                <div className="w-64">
                  <label className="text-xs text-slate-400 dark:text-slate-400">Match on</label>
                  <Input value={draft.match} onChange={(e) => setDraft({ ...draft, match: e.target.value })}
                    placeholder="sam@unitedcloud.ca" className="h-8 font-mono text-xs"
                    data-testid="prod-roster-new-match" />
                </div>
                <Button size="sm" onClick={add} disabled={busy} data-testid="prod-roster-add-btn">
                  {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                  Add person
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
